import { Editor } from '@tiptap/core';
import { forwardRef, RefObject, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import {
	ActionDecorations,
	ActionFeedback,
	ActionInput,
	ActionMedia,
	ActionPrivacy,
	ActionSubmit,
	ActionTags,
	ActionText,
	Privacy,
	SocialAttachment,
	SocialAttachmentAPIData,
} from '@bonusly/common-action-input';
import { Box, Hide, HStack, ModalFooter, rem, Show, useBreakpointValue } from '@bonusly/design-system';
import { useFeatureFlag } from '@bonusly/who-am-i';

import { DimensionKey } from '../../../../../queries/recognition-analysis';
import { DraftAssistAccordion } from '../../../gratitude-gauge/draft-assist-accordion';
import { DraftAssistPopout } from '../../../gratitude-gauge/draft-assist-popout';
import { useDraftAssist } from '../../../gratitude-gauge/hooks/use-draft-assist';
import { Meter } from '../../../gratitude-gauge/meter';
import { ScoreBoxAccordion } from '../../../gratitude-gauge/scorebox-accordion';
import { ScoreBoxPopout } from '../../../gratitude-gauge/scorebox-popout';
import { useGratitudeGauge } from '../../../gratitude-gauge/use-gratitude-gauge';
import { GiveBoxAttachment } from '../../events';
import { useGiveBox } from '../../hooks/use-give-box';
import { loadDraft, saveDraft } from '../../utils/recognition-storage';
import { GiveModalContentBody } from './give-modal-content-body';

interface GiveModalContentProps {
	content?: any;
	initialAttachment?: GiveBoxAttachment;
	onSuccess: VoidFunction;
	setIsSubmitting: (isSubmitting: boolean) => void;
	onVideoAttachmentChange?: (hasVideo: boolean) => void;
	via?: string;
	/** Ref set by the parent before calling onClose() or onSuccess(), so the unmount cleanup
	 *  reads the intended outcome rather than always defaulting to 'abandoned'. */
	pendingOutcomeRef?: RefObject<'submitted' | 'discarded' | 'abandoned'>;
}

export interface GiveModalContentHandle {
	hasVideoAttachment: () => boolean;
	saveDraft: () => void;
}

const DRAFT_ASSIST_MOBILE_CUTOFF = 1240;
const GAUGE_MOBILE_CUTOFF = 1440;

export const GiveModalContent = forwardRef<GiveModalContentHandle, GiveModalContentProps>(
	({ content, initialAttachment, onSuccess, setIsSubmitting, onVideoAttachmentChange, via, pendingOutcomeRef }, ref) => {
		const [videoAttachment, setVideoAttachment] = useState<boolean>(false);
		const gratitudeGauge = useGratitudeGauge();
		const isDraftAssistEnabled = useFeatureFlag('bizy-draft-assist');
		const { track } = useAnalytics();

		const [recipients, setRecipients] = useState<{ id: string; name: string }[]>([]);
		const [attachedValues, setAttachedValues] = useState<string[]>([]);
		// Starts as false; DraftAssistPopout and DraftAssistAccordion only call onOpenChange on
		// user interaction, never on mount, and they always start closed (no localStorage restore).
		const [isGaugeOpen, setIsGaugeOpen] = useState(false);
		const [requestedDimensionKey, setRequestedDimensionKey] = useState<DimensionKey | null>(null);

		const isMobile = useBreakpointValue({ base: true, sm: false });

		const draftAssist = useDraftAssist({
			dimensions: gratitudeGauge.dimensions,
			draftText: gratitudeGauge.text,
			recipientIds: recipients.map((r) => r.id),
			attachedValues,
			isScoring: gratitudeGauge.isFetching,
			// LLM fires only when the user explicitly opens the panel. Both components start
			// closed when draft assist is on (no localStorage restore), so isGaugeOpen and
			// the child open state are always in sync — no mount-time desync possible.
			isOpen: isGaugeOpen,
			requestedDimensionKey,
		});

		const handleSuccess = useCallback(() => {
			void draftAssist.signalOutcome('submitted');
			onSuccess();
		}, [draftAssist.signalOutcome, onSuccess]);

		const config = useGiveBox({ onSuccess: handleSuccess, via, draftAssistInteractionIds: draftAssist.draftAssistInteractionIds });

		const handleGaugeOpenChange = useCallback(
			(nextIsOpen: boolean) => {
				if (nextIsOpen && draftAssist.hasUnmetDimension) {
					track(AnalyticsEvent.GratitudeGauge.DraftAssistHintClicked, {
						dimensionKey: draftAssist.hintDimensionKey,
					});
				}
				setIsGaugeOpen(nextIsOpen);
				if (!nextIsOpen) {
					setRequestedDimensionKey(null);
				}
			},
			[draftAssist.hasUnmetDimension, draftAssist.hintDimensionKey, track]
		);

		const handleDimensionExpanded = useCallback((key: string | null) => {
			setRequestedDimensionKey(key as DimensionKey | null);
		}, []);

		const handleInsertSuggestion = useCallback(
			async (suggestion: string) => {
				if (!suggestion) return;
				const input = config.ref.current;
				if (input) {
					input.insertWithHighlight(suggestion);
					await draftAssist.accept();
				}
			},
			[config.ref, draftAssist.accept]
		);

		// Track the latest editor content for draft preservation
		const editorContentRef = useRef<unknown | null>(null);
		// Load draft on mount (only if no explicit content was passed via props)
		// This handles restoring state after accidental modal dismissal
		const draft = useMemo(() => {
			if (!content && !initialAttachment) {
				return loadDraft();
			}
			return null;
			// eslint-disable-next-line react-hooks/exhaustive-deps -- only check on mount
		}, []);

		// Compute effective values: use draft if available, otherwise use props
		const effectiveContent = draft?.content ?? content;
		const effectiveAttachment = useMemo(() => {
			if (initialAttachment) return initialAttachment;
			if (draft?.attachment) {
				return new SocialAttachment(draft.attachment as SocialAttachmentAPIData);
			}
			return undefined;
		}, [draft?.attachment, initialAttachment]);
		const effectivePrivacy = (draft?.privacy as Privacy) ?? undefined;

		// Initialize editorContentRef with loaded content so drafts can be re-saved
		// even if the user doesn't make any edits (refreshes the expiration timer)
		useEffect(() => {
			if (effectiveContent && !editorContentRef.current) {
				editorContentRef.current = effectiveContent;
			}
		}, [effectiveContent]);

		// this hook allows us to expose data to parent compoenents. Specifically, they need to know
		// if there is a video attachment so they can confirm the user wants to delete it before closing
		// the modal. Also exposes saveDraft for overlay click handling.
		useImperativeHandle(
			ref,
			() => ({
				hasVideoAttachment: () => videoAttachment,
				saveDraft: () => {
					const editorContent = editorContentRef.current;
					if (!editorContent) return;

					const data = config.ref.current?.data();
					let attachment: unknown = undefined;

					// Only save GIF attachments (social), not videos
					if (data?.attachment && data.attachment.category === 'social') {
						attachment = data.attachment.toJSON();
					}

					saveDraft(editorContent, attachment, data?.privacy);
				},
			}),
			[config.ref, videoAttachment]
		);

		useEffect(() => {
			return () => {
				// Read the outcome the parent committed to before triggering the close animation.
				// Falls back to 'abandoned' when no parent ref is provided (e.g. standalone usage).
				void draftAssist.signalOutcome(pendingOutcomeRef?.current ?? 'abandoned');
			};
			// eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally runs on unmount only
		}, []);

		useEffect(() => {
			setIsSubmitting(config.isSubmitting);
		}, [config.isSubmitting, setIsSubmitting]);

		// The `checkVideoAttachment` function is needed to maintain the state of the video attachment within the GiveModalContent component.
		// It serves the following purposes:
		// 1. State Synchronization: It ensures that the `videoAttachment` state is always in sync with the actual data present in the ActionInput component.
		//    This is important because the presence of a video attachment affects the behavior of the modal, particularly when the user attempts to close it.
		// 2. User Confirmation: By detecting changes in the video attachment state, it allows the parent component to prompt the user for confirmation
		//    before closing the modal if a video is attached. This prevents accidental loss of data, which is especially important for video files that
		//    might take significant time and resources to upload.
		// 3. Real-time Updates: The function is called immediately and at regular intervals to check for changes in the attachment state.
		//    This real-time checking is necessary because the attachment state can change due to user actions or asynchronous operations.
		// 4. Callback Invocation: It invokes the `onVideoAttachmentChange` callback whenever there is a change in the video attachment state.
		//    This allows other components or hooks to react to changes, enabling a more dynamic and responsive UI.
		const checkVideoAttachment = useCallback(() => {
			const data = config.ref.current?.data();
			const category = data?.attachment?.category;
			const hasVideoOrAudio = category === 'video' || category === 'audio';

			// Ughh I don't like doing this at all. We really need to fix the state management with the give box
			if (videoAttachment && !hasVideoOrAudio) {
				gratitudeGauge.onChangeTranscription('');
			}

			if (hasVideoOrAudio !== videoAttachment) {
				setVideoAttachment(hasVideoOrAudio);
				onVideoAttachmentChange?.(hasVideoOrAudio);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps -- gratitudeGauge returns unstable object reference, intentionally omitted
		}, [config.ref, onVideoAttachmentChange, videoAttachment]);

		useEffect(() => {
			// Check immediately
			checkVideoAttachment();
			// Set up an interval to check for changes. We're operation outside
			// the ActionMedia provider and don't have access to media state.
			const interval = setInterval(checkVideoAttachment, 100);
			// Cleanup the interval when the component unmounts
			return () => clearInterval(interval);
		}, [checkVideoAttachment]);

		// Sync any recipients already present in restored draft content.
		// onUpdate doesn't fire for initial content, so we read once on mount.
		useEffect(() => {
			const data = config.ref.current?.data();
			if (!data) return;
			const initial = data.recipients.map((r) => ({ id: r.id, name: r.displayName }));
			if (initial.length > 0) setRecipients(initial);
			// eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally runs once on mount
		}, []);

		const draftAssistProp = useMemo(() => {
			const suggestion = draftAssist.suggestion ?? undefined;
			return {
				suggestion,
				dimensionKey: draftAssist.dimensionKey ?? undefined,
				isLoading: draftAssist.isLoading,
				loadingDimensionKey: requestedDimensionKey,
				onAdd: suggestion ? () => handleInsertSuggestion(suggestion) : undefined,
				onRegenerate: draftAssist.regenerate,
			};
		}, [draftAssist.suggestion, draftAssist.dimensionKey, draftAssist.isLoading, draftAssist.regenerate, handleInsertSuggestion, requestedDimensionKey]);

		const handleUpdate = ({ editor }: { editor: Editor }) => {
			const rawText = editor.getText();
			gratitudeGauge.onChangeText(rawText.trim());
			// Store the latest editor content for draft preservation
			editorContentRef.current = editor.getJSON();
			// Sync recipients — adding/removing a recipient node fires onUpdate,
			// so this is event-driven rather than polled.
			const data = config.ref.current?.data();
			if (data) {
				const next = data.recipients.map((r) => ({ id: r.id, name: r.displayName }));
				setRecipients((prev) => {
					const prevKey = prev.map((r) => r.id).join(',');
					const nextKey = next.map((r) => r.id).join(',');
					return prevKey === nextKey ? prev : next;
				});
			}
			// Read hashtags directly from the current editor document. config.ref.current?.data()
			// returns inputState from ActionInput's previous render — TipTap fires onUpdate before
			// ActionInput re-renders, so that path always lags one change behind.
			const nextHashtags: string[] = [];
			editor.view.state.doc.descendants((node) => {
				if (node.type.name === 'hashtag') nextHashtags.push(node.attrs.id as string);
			});
			setAttachedValues((prev) => {
				const prevKey = prev.join(',');
				const nextKey = nextHashtags.join(',');
				return prevKey === nextKey ? prev : nextHashtags;
			});
		};

		const mobileBreakpoint = isDraftAssistEnabled ? DRAFT_ASSIST_MOBILE_CUTOFF : GAUGE_MOBILE_CUTOFF;

		return (
			<ActionInput
				autofocus="end"
				content={effectiveContent}
				initialAttachment={effectiveAttachment}
				initialPrivacy={effectivePrivacy}
				onUpdate={handleUpdate}
				{...config}
			>
				<GiveModalContentBody display="flex" flexDirection="column" flex="1" px={{ base: 0, sm: 6 }} pt={{ base: 4, sm: 0 }} pb={0}>
					<Show breakpoint={`(min-width: ${mobileBreakpoint}px)`}>
						{isDraftAssistEnabled ? (
							<DraftAssistPopout
								dimensions={gratitudeGauge.dimensions}
								isLoading={draftAssist.isLoading}
								isScoring={gratitudeGauge.isFetching}
								text={gratitudeGauge.text}
								draftAssist={draftAssistProp}
								hasUnmetDimension={draftAssist.hasUnmetDimension}
								hintDimensionKey={draftAssist.hintDimensionKey}
								onOpenChange={handleGaugeOpenChange}
								onDimensionExpanded={handleDimensionExpanded}
							/>
						) : (
							<ScoreBoxPopout
								dimensions={gratitudeGauge.dimensions}
								isLoading={gratitudeGauge.isFetching}
								text={gratitudeGauge.text}
								onOpenChange={setIsGaugeOpen}
							/>
						)}
					</Show>

					{/* Visibility dropdown at the top - same location for mobile and desktop */}
					<Box px={{ base: 4, sm: 0 }}>
						<ActionPrivacy />
					</Box>

					{/* Text input area */}
					<Box flex="1" position="relative" display="flex" flexDirection="column" px={{ base: 4, sm: 0 }}>
						<ActionText
							height="100%"
							inputStyles={
								isMobile
									? {
											flex: '1',
											minHeight: rem(80),
											display: 'flex',
											flexDirection: 'column',
											position: 'relative',
											padding: '0.5rem 0',
										}
									: { minHeight: rem(120), padding: '0.5rem 0' }
							}
							display="flex"
							flexDirection="column"
							flex="1"
							minHeight={0}
							overflow="hidden"
						/>
						<ActionFeedback mb={2} />
						<ActionMedia w="full" flexShrink={0} />
					</Box>
				</GiveModalContentBody>

				<ModalFooter display="block" px={{ base: 0, sm: 6 }} py={4} overflow="hidden" borderBottomRadius="10px">
					{/* Desktop: Bottom toolbar - Tags on left, Action buttons on right */}
					<HStack justifyContent="space-between" mb={2} gap={2} px={{ base: 4, md: 0 }}>
						<ActionTags />
						<ActionDecorations />
					</HStack>

					{/* Full width submit button */}
					<Box px={{ base: 4, sm: 0 }}>
						<ActionSubmit size="lg" w="full" />
					</Box>

					<Hide breakpoint={`(min-width: ${mobileBreakpoint}px)`}>
						<Box px={{ base: 4, sm: 0 }} pt={2} pb={2}>
							<Box borderRadius="md" overflow="hidden" borderWidth="1px" borderColor="border.default">
								{isDraftAssistEnabled ? (
									<DraftAssistAccordion
										dimensions={gratitudeGauge.dimensions}
										isLoading={draftAssist.isLoading}
										isScoring={gratitudeGauge.isFetching}
										text={gratitudeGauge.text}
										draftAssist={draftAssistProp}
										hasUnmetDimension={draftAssist.hasUnmetDimension}
										hintDimensionKey={draftAssist.hintDimensionKey}
										onOpenChange={handleGaugeOpenChange}
										onDimensionExpanded={handleDimensionExpanded}
									/>
								) : (
									<ScoreBoxAccordion
										dimensions={gratitudeGauge.dimensions}
										isLoading={gratitudeGauge.isFetching}
										text={gratitudeGauge.text}
										onOpenChange={setIsGaugeOpen}
									/>
								)}
							</Box>
						</Box>
					</Hide>

					<Show breakpoint={`(min-width: ${mobileBreakpoint}px)`}>
						<Box mx={-6} mb={-4} mt={2}>
							<Meter matches={videoAttachment ? gratitudeGauge.totalDimensions : gratitudeGauge.totalMatches} total={gratitudeGauge.totalDimensions} />
						</Box>
					</Show>
				</ModalFooter>
			</ActionInput>
		);
	}
);

GiveModalContent.displayName = 'GiveModalContent';
